import { gql } from "@apollo/client";
import {
  CellExecutionState,
  CellId,
  MagicEventId,
  MagicEventStatus,
  OrgRole,
  ProjectLanguage,
} from "@hex/common";
import { useCellData } from "../components/output/useCellData.js";
import {
  useLatestMagicEventForCellSelector,
  useMagicEventsSelector,
} from "../hex-version-multiplayer/state-hooks/magicEventStateHooks.js";
import { useCreateSmartEdit } from "../magic/useCreateSmartEdit.js";
import { useSelector } from "../redux/hooks.js";
import { MagicEventMP } from "../redux/slices/hexVersionMPSlice.js";
import { magicSelectors } from "../redux/slices/logicViewSlice.js";
import { shallowEqual } from "../util/index.js";
import { useProjectContext } from "../util/projectContext.js";

import { useAppSessionCellIdMap } from "./appSession/useAppSessionCellIdMap.js";
import { useGetUserForMagicQuery } from "./magicHooks.generated.js";
import { useCurrentUser } from "./me/useCurrentUser.js";

gql`
  query GetUserForMagic {
    me {
      id
      userMagicConfig
    }
  }
`;

export const useMagicCellChainsEnabled = (): boolean => {
  const { language } = useProjectContext();
  return language === ProjectLanguage.PYTHON;
};

export function useUserForMagic(): {
  loading: boolean;
  magicEnabled: boolean;
  magicTypeaheadAllowed: boolean;
  onboardedToMagic: boolean;
  refetch: () => void;
} {
  const { data, loading, refetch } = useGetUserForMagicQuery();
  return {
    refetch,
    loading,
    magicEnabled: data?.me?.userMagicConfig.magicEnabled ?? false,
    magicTypeaheadAllowed:
      data?.me?.userMagicConfig.magicTypeaheadAllowed ?? false,
    onboardedToMagic: data?.me?.userMagicConfig.onboardedToMagic ?? false,
  };
}

export const useCanAccessDataManager = () => {
  const user = useCurrentUser();

  return user?.orgRole === OrgRole.ADMIN;
};

export const usePendingMagicEvents = () => {
  const pending = useMagicEventsSelector({
    equalityFn: shallowEqual,
    selector: (magicEventState) => {
      return Object.values(magicEventState).some(
        (event) =>
          event &&
          event?.status !== MagicEventStatus.REVIEWED &&
          event?.status !== MagicEventStatus.CANCELLED,
      );
    },
  });
  return pending;
};

export function useSingleCellMagicEdit(cellId: CellId) {
  const lastMagicEvent = useLatestMagicEventForCellSelector({
    cellId,
    selector: (magicEventState: MagicEventMP | undefined) => magicEventState,
    equalityFn: shallowEqual,
    safe: true,
  });

  const { acceptSmartEdit, cancelEdit, rejectSmartEdit } = useCreateSmartEdit({
    cellId,
    magicEventId: lastMagicEvent?.id,
  });

  const magicStateLoading = useSelector((state) => {
    if (!cellId) return false;
    // This seems wrong to me? like it should be instead using useMagicCellStateGetter
    // so that the loading state is actually correctly resolved
    const mState = magicSelectors.selectMagicCellState(state, cellId);
    return mState.smartEditLoading;
  });

  // If the latest magic event exists we ALWAYS rely on that magic event for
  // state, and then if one doesn't exist, we use the redux state.
  // Practically this should only briefly happen when we kick off a request
  const smartEditLoading = lastMagicEvent
    ? lastMagicEvent.status === MagicEventStatus.LOADING
    : magicStateLoading;

  return {
    acceptSmartEdit,
    smartEditLoading,
    rejectSmartEdit,
    cancelEdit,
  };
}

/*
 * This tells us if the output from a given cell was the result of running a pending magic edit or not
 **/
export function useOutputIsFromPendingMagicEvent(cellId?: CellId): boolean {
  const appSessionCellMap = useAppSessionCellIdMap();
  const appSessionCellId = cellId ? appSessionCellMap[cellId] : undefined;
  // eslint-disable-next-line hex/explicitly-handle-output-loading-state -- handles null case itself
  const { outputs, state } = useCellData({ appSessionCellId });

  const hasMagicOutputs = !!outputs.find((o) =>
    o.outputFragment.requestId?.startsWith("magic:"),
  );

  return hasMagicOutputs && state === CellExecutionState.IDLE;
}

export type UseSelectMagicEventStreamingArgs = {
  magicEventId: MagicEventId | undefined;
};
